<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file

Fork rework: the header controls are stacked vertically, every control
full width on an 8/12px rhythm — the previous two-column squeeze clipped
the search label and wrapped the group select. The search field also
feeds a debounced live query into the filter, which drives the panel's
"name matches / uses or produces" sections while typing.
-->
<script setup lang="ts">
import type {GameItem} from '#types/game-data';
import {injectGameData} from '@/scripts/data';
import {injectFilter} from '@/scripts/filter';
import {injectSettings} from '@/scripts/settings';
import {mdiApps, mdiClose, mdiFormatListBulleted, mdiViewList} from '@mdi/js';
import {computed, nextTick, onBeforeUnmount, ref, unref, watch} from 'vue';

const gameData = injectGameData();
const filter = injectFilter();
const settings = injectSettings();

const {minTier, maxTier} = gameData.gameDescription;
const allTiers = (maxTier > minTier) ? Array.from(Array(1 + maxTier - minTier), (el, i: number) => (i + minTier)) : [];

type SuggestionKind = 'factory' | 'product';
type Suggestion = {
    value: string;
    title: string;
    kind: SuggestionKind;
    item: GameItem;
};

const MAX_SUGGESTIONS = 15;
const search = ref('');
const selectedValue = ref<string | undefined>(undefined);

//plain typing drives the panel's live sections (debounced); explicit
//picks and the clear button reset it immediately
let liveQueryTimer: number | undefined = undefined;
function setLiveQuery(value: string) {
    if(liveQueryTimer !== undefined) {
        window.clearTimeout(liveQueryTimer);
        liveQueryTimer = undefined;
    }
    filter.liveQuery = value.trim();
}
watch(search, (value) => {
    if(liveQueryTimer !== undefined)
        window.clearTimeout(liveQueryTimer);
    liveQueryTimer = window.setTimeout(() => {
        liveQueryTimer = undefined;
        filter.liveQuery = (value || '').trim();
    }, 150);
});
onBeforeUnmount(() => {
    if(liveQueryTimer !== undefined)
        window.clearTimeout(liveQueryTimer);
});

//typeahead: factories and products in one ranked list.
//rank: label prefix > name prefix > label substring > name substring,
//factories before products inside the same rank, then by label.
const suggestions = computed<Suggestion[]>(() => {
    const query = unref(search).trim().toLowerCase();
    if(!query)
        return [];
    const scored: {suggestion: Suggestion; rank: number}[] = [];
    const consider = (kind: SuggestionKind, item: GameItem) => {
        const label = item.lowerLabel;
        const name = item.name.toLowerCase();
        let rank: number;
        if(label.startsWith(query))
            rank = 0;
        else if(name.startsWith(query))
            rank = 1;
        else if(label.indexOf(query) > -1)
            rank = 2;
        else if(name.indexOf(query) > -1)
            rank = 3;
        else
            return;
        scored.push({
            suggestion: {value: `${kind}:${item.name}`, title: item.label, kind, item},
            rank,
        });
    };
    for(const item of gameData.gameFactoriesArray)
        consider('factory', item);
    for(const item of gameData.gameItemsArray) {
        //items with a recipe dictionary are already listed as factories
        if(!item.recipeDictionary)
            consider('product', item);
    }
    scored.sort((a, b) => (
        (a.rank - b.rank) ||
        ((a.suggestion.kind === b.suggestion.kind) ? 0 : ((a.suggestion.kind === 'factory') ? -1 : 1)) ||
        a.suggestion.title.localeCompare(b.suggestion.title)
    ));
    return scored.slice(0, MAX_SUGGESTIONS).map(({suggestion}) => suggestion);
});

//the item the list is currently filtered by (may also be set from elsewhere)
const activeItem = computed(() => {
    const key = filter.key;
    return key ? gameData.getGameItem(key) : undefined;
});
const activeKind = computed<SuggestionKind | undefined>(() => {
    const item = unref(activeItem);
    if(!item)
        return undefined;
    return item.recipeDictionary ? 'factory' : 'product';
});

function onSelectSuggestion(value: string | null) {
    if(!value)
        return;
    const name = value.substring(value.indexOf(':') + 1);
    const item = gameData.getGameItem(name);
    if(!item)
        return;
    //for a factory this isolates the factory itself (factories are not
    //recipe inputs/outputs of other factories); for a product it lists
    //every factory consuming or producing it, direction narrows it further
    filter.key = item.name;
    filter.direction = 0;
    setLiveQuery('');
    nextTick(() => { search.value = ''; });
}

function resetSearch() {
    search.value = '';
    selectedValue.value = undefined;
    filter.key = undefined;
    filter.direction = 0;
    setLiveQuery('');
}

const groupModes = [
    {title: 'Categorised', value: 'category'},
    {title: 'By tier', value: 'tier'},
    {title: 'By output', value: 'output'},
    {title: 'No grouping', value: 'none'},
];
</script>

<template>
    <div class="filter-root">
        <v-autocomplete
            v-model="selectedValue"
            v-model:search="search"
            clearable
            no-filter
            hide-no-data
            hide-details
            density="comfortable"
            label="Search factories &amp; products"
            placeholder="Search factories &amp; products"
            item-title="title"
            item-value="value"
            :items="suggestions"
            @update:model-value="onSelectSuggestion"
            @click:clear="resetSearch"
            @keydown.esc="resetSearch"
        >
            <template #item="{ props, item }">
                <v-list-item v-bind="props" :title="item.raw.title">
                    <template #prepend>
                        <icon-component class="rounded icon-div" :image="item.raw.item.image" />
                    </template>
                    <template #append>
                        <v-chip v-if="(item.raw.kind === 'product')" size="x-small" label>
                            product
                        </v-chip>
                    </template>
                </v-list-item>
            </template>
            <template #selection />
            <template #prepend-inner>
                <icon-component
                    v-if="activeItem"
                    class="rounded icon-div"
                    :image="activeItem.image"
                />
            </template>
        </v-autocomplete>

        <div v-if="activeItem" class="active-filter">
            <span class="active-filter-label text-truncate" :title="activeItem.label">
                {{ activeItem.label }}
            </span>
            <v-chip v-if="(activeKind === 'product')" size="x-small" label>
                product
            </v-chip>
            <v-spacer />
            <v-btn
                density="compact"
                variant="text"
                :icon="mdiClose"
                title="Clear filter"
                @click="resetSearch"
            />
        </div>
        <div v-if="(activeKind === 'product')">
            <v-btn-toggle
                :model-value="filter.direction"
                class="segmented-toggle"
                density="comfortable"
                group
                mandatory
                variant="outlined"
                @update:model-value="(value) => { if(value !== undefined) filter.direction = value; }"
            >
                <v-btn :value="-1">
                    In
                </v-btn>
                <v-btn :value="0">
                    Both
                </v-btn>
                <v-btn :value="1">
                    Out
                </v-btn>
            </v-btn-toggle>
        </div>

        <div>
            <div class="control-label">
                Display
            </div>
            <v-btn-toggle
                v-model="settings.leftPanelDisplayMode"
                class="segmented-toggle"
                density="comfortable"
                group
                mandatory
                variant="outlined"
                title="List display mode"
            >
                <v-btn value="both" title="Icons + names">
                    <v-icon :icon="mdiViewList" />
                    <span class="segment-label">Both</span>
                </v-btn>
                <v-btn value="names" title="Names only">
                    <v-icon :icon="mdiFormatListBulleted" />
                    <span class="segment-label">Names</span>
                </v-btn>
                <v-btn value="icons" title="Icons only">
                    <v-icon :icon="mdiApps" />
                    <span class="segment-label">Icons</span>
                </v-btn>
            </v-btn-toggle>
        </div>

        <div>
            <div class="control-label">
                Group
            </div>
            <v-select
                v-model="filter.groupMode"
                density="comfortable"
                hide-details
                :items="groupModes"
            />
        </div>

        <div v-if="(maxTier > minTier)">
            <div class="control-label">
                Tier
            </div>
            <v-select
                v-model="filter.tier"
                clearable
                hide-details
                density="comfortable"
                :items="allTiers"
            >
                <template #prepend-item>
                    <v-btn-toggle
                        :model-value="filter.tierEqual"
                        density="compact"
                        group
                        mandatory
                        variant="outlined"
                        class="ml-2"
                        @update:model-value="(value) => { if(value !== undefined) filter.tierEqual = value; }"
                    >
                        <v-btn :value="-1">
                            &lt;=
                        </v-btn>
                        <v-btn :value="0">
                            =
                        </v-btn>
                        <v-btn :value="1">
                            &gt;=
                        </v-btn>
                    </v-btn-toggle>
                </template>
            </v-select>
        </div>
    </div>
</template>

<style scoped>
.filter-root {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    box-sizing: border-box;
}
.icon-div {
    display: inline-block;
}
.control-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.65;
    margin-bottom: 4px;
}
.segmented-toggle {
    width: 100%;
}
.segmented-toggle :deep(.v-btn) {
    flex: 1 1 0;
}
.segment-label {
    margin-left: 6px;
}
.active-filter {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: -4px;
}
.active-filter-label {
    font-size: 13px;
    min-width: 0;
}
</style>
